/**
 * Protokolleinträge in lesbare deutsche Zeilen — und Zeitstempel in etwas, das man
 * beim Überfliegen versteht.
 *
 * Bewusst hier und nicht in der Komponente: Formulierungen sind das, woran eine
 * Oberfläche gemessen wird, und als reine Funktionen lassen sie sich prüfen, ohne
 * eine Ansicht zu rendern.
 *
 * Der Server liefert `from`/`to` bereits anzeigefertig (Status als Beschriftung, der
 * Verantwortliche als Name). Hier wird nur noch zusammengesetzt.
 */

import {
  FEATURE_FIELD_LABELS,
  type ChangeActivityDto,
  type FeatureChangeEntry
} from '@pmpro/shared';

/** Ein nicht gesetzter Wert — „vorher niemand, jetzt Anna Beck". */
const LEER = '—';

function feldZeile(entry: FeatureChangeEntry): string {
  const label = FEATURE_FIELD_LABELS[entry.field];

  // Bei der Beschreibung steht im Protokoll nur, DASS sie sich geändert hat: Der
  // aktuelle Text steht vollständig weiter oben auf der Seite.
  if (entry.omitted) return `${label} geändert`;

  return `${label}: ${entry.from ?? LEER} → ${entry.to ?? LEER}`;
}

/**
 * Die Zeilen, die ein Protokolleintrag im Strom anzeigt.
 *
 * Die Art des Vorgangs (`kind`) wird NICHT zusätzlich als Satz vorangestellt: „hat
 * den Status geändert" über „Status: In Entwicklung → Testing" sagt zweimal
 * dasselbe. Nur wo es keine Feldwerte gibt, tritt ein Satz an ihre Stelle.
 */
export function aenderungsZeilen(eintrag: ChangeActivityDto): string[] {
  if (eintrag.kind === 'created') return ['Feature angelegt'];

  if (eintrag.changes.length === 0) {
    return eintrag.kind === 'evaluation'
      ? ['Neu bewertet — die Priorität bleibt gleich']
      : ['Geändert'];
  }

  return eintrag.changes.map(feldZeile);
}

function uhrzeit(datum: Date): string {
  return datum.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function istGleicherTag(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Zeitpunkt eines Eintrags: „heute, 14:02", „gestern, 09:30" oder das volle Datum.
 *
 * `jetzt` ist ein Parameter, damit die Funktion prüfbar bleibt — eine Formatierung,
 * die heimlich die Systemuhr liest, lässt sich nur an einem bestimmten Tag testen.
 */
export function zeitpunkt(iso: string, jetzt: Date = new Date()): string {
  const datum = new Date(iso);
  if (Number.isNaN(datum.getTime())) return '';

  if (istGleicherTag(datum, jetzt)) return `heute, ${uhrzeit(datum)}`;

  const gestern = new Date(jetzt);
  gestern.setDate(gestern.getDate() - 1);
  if (istGleicherTag(datum, gestern)) return `gestern, ${uhrzeit(datum)}`;

  return `${datum.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })}, ${uhrzeit(datum)}`;
}
